import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientNoteEntity } from './client-note.entity';
import { ClientEntity } from './client.entity';

/**
 * Sales visit record — when a salesperson visits a client. Despite the
 * `m_sm_` prefix (legacy SM1 carryover) this is an actively-used CRM
 * entity in both the new app (`clients.service.ts:createVisit`,
 * scorecard reports, goals reports, AI agent tools) AND the old app
 * (`~/development/supplymover/src/service/client-visit.service.ts`).
 *
 * Distribution refactor history: Rafi previously added 9 routing/delivery
 * columns to this table (`m_sm_fk_route_stop__id`,
 * `m_sm_fk_execution__id`, `m_sm_sequence`, `m_sm_visit_status`,
 * `m_sm_delivery_failure_reason`, `m_sm_proof_signature`,
 * `m_sm_proof_photo`, `m_sm_actual_arrival`, `m_sm_actual_departure`)
 * and the routes module created visit rows on delivery-stop completion
 * — conflating sales visits with delivery stops. All those columns are
 * dropped; delivery completion data lives natively on `trip_stop` now.
 */
@Entity('m_sm_client_visit')
@Index('ix_client_user', ['clientId', 'workdUserId'])
export class ClientVisitEntity extends BaseEntity {
	@Column({ name: 'm_sm_fk_client__id', type: 'int', unsigned: true })
	public clientId!: number;

	@Column({ name: 'm_sm_workdUserId', type: 'int', unsigned: true })
	public workdUserId!: number;

	@Column({ name: 'm_sm_date', type: 'datetime', nullable: true })
	public date?: Date;

	@Column({
		name: 'm_sm_latitude',
		type: 'decimal',
		precision: 10,
		scale: 8,
		nullable: true,
	})
	public latitude?: number | null;

	@Column({
		name: 'm_sm_longitude',
		type: 'decimal',
		precision: 11,
		scale: 8,
		nullable: true,
	})
	public longitude?: number | null;

	/**
	 * PD-15918 (decision 22) — how far the rep's device was from the client's
	 * address when the visit was logged, in meters (Haversine), and whether
	 * that distance falls inside the delivery GPS threshold
	 * (`isGpsWithinThreshold`). Computed on EVERY visit create regardless of
	 * how it was logged; null distance = either side had no coordinates.
	 */
	@Column({ name: 'gpsDistanceMeters', type: 'int', nullable: true })
	public gpsDistanceMeters?: number | null;

	@Column({ name: 'gpsValidated', type: 'boolean', default: false })
	public gpsValidated!: boolean;

	/**
	 * The sales trip stop that produced this visit (decision 2). Nullable —
	 * ad-hoc visits have no stop. Kept as a plain column (no relation) so the
	 * visit entity does not import the trip graph; `SalesTripStopEntity`
	 * carries `clientVisitId` back the other way.
	 */
	@Column({
		name: 'fk_sales_trip_stop__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public salesTripStopId?: number | null;

	@ManyToOne(() => ClientEntity, (client) => client.clientVisits)
	@JoinColumn({ name: 'm_sm_fk_client__id' })
	public fkClient!: ClientEntity;

	/**
	 * PD-14327 — the client notes logged with this visit. The Log Visit flow
	 * writes at most one, but this is a one-to-many because `client_note` is
	 * the owning side and nothing stops a second note being linked later.
	 */
	@OneToMany(() => ClientNoteEntity, (clientNote) => clientNote.fkClientVisit)
	public clientNotes!: Array<ClientNoteEntity>;
}
